import { useToast } from "@/hooks/use-toast";
import {
  COACH_PICKER_LIMIT,
  coachService,
  type AuditLogEntry,
  type PaginatedCoachesResponse,
} from "@/lib/api/coachService";
import { Coach, CoachType } from "@/lib/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const COACHES_QUERY_KEY = ["/api/coaches"];

interface UseCoachesOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  enabled?: boolean;
}

export function useCoaches(options: UseCoachesOptions = {}) {
  const {
    page = 1,
    limit = COACH_PICKER_LIMIT,
    search,
    status,
    enabled = true,
  } = options;

  return useQuery<PaginatedCoachesResponse>({
    queryKey: [...COACHES_QUERY_KEY, page, limit, search ?? "", status ?? ""],
    queryFn: async () => {
      return await coachService.getCoaches({ page, limit, search, status });
    },
    enabled,
  });
}

export function useCoachContent(coachId: string | undefined) {
  return useQuery({
    queryKey: [...COACHES_QUERY_KEY, coachId, "content"],
    enabled: !!coachId,
    queryFn: () => coachService.getCoachContent(coachId as string),
  });
}

export function useCoachAuditHistory(coachId: string) {
  return useQuery<AuditLogEntry[]>({
    queryKey: [...COACHES_QUERY_KEY, coachId, "audit-history"],
    enabled: false, // disabled endpoitn not impmeneted, please enable when implment this endpoint
    queryFn: async () => {
      return [];
    },
  });
}

export function useInviteCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: { name: string; email: string; coachType: CoachType }) =>
      coachService.inviteCoach(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach invitation sent successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send coach invitation",
        description:
          error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
      file,
    }: {
      id: string;
      data: Partial<Coach>;
      file?: File;
    }) => coachService.updateCoach(id, data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach updated successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update coach",
        description:
          // The server's nested validation message starts with the DTO path
          // ("socialLinks.5.url for Website must be …"); show "Website must be …".
          error?.message?.replace(/socialLinks\.\d+\.url for /g, "") ||
          "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useSuspendCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      coachService.suspendCoach(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach suspended" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to suspend coach",
        description:
          error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useRestoreCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => coachService.restoreCoach(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach restored" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to restore coach",
        description:
          error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useRestoreDeletedCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => coachService.restoreDeletedCoach(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach restored successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to restore deleted coach",
        description:
          error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteCoach() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      coachService.deleteCoach(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Coach deleted successfully" });
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to delete coach";
      toast({
        title: "Failed to delete coach",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
}
